/**
 * Interactive Video Carousel
 * Simple scroll-snap carousel controller for the interactive-video-carousel section.
 * Controls prev/next arrows, dots, and disables buttons at track boundaries.
 */

class InteractiveVideoCarousel {
  /**
   * @param {HTMLElement} root - Section root element ([data-interactive-video-carousel])
   */
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.prevBtn = root.querySelector('[data-carousel-prev]');
    this.nextBtn = root.querySelector('[data-carousel-next]');
    this.dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));
    this.slides = Array.from(this.track.children);

    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);

    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(this.track);

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this._scrollBy(-1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this._scrollBy(1));
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.carouselDot, 10);
        this._scrollToSlide(index);
      });
    });

    this._updateState();
  }

  /** Scroll the track by +1 or -1 slides. */
  _scrollBy(direction) {
    const slideWidth = this._getSlideWidth();
    if (!slideWidth) return;
    this.track.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
  }

  /** Scroll to a specific slide index. */
  _scrollToSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const trackLeft = this.track.getBoundingClientRect().left;
    const slideLeft = slide.getBoundingClientRect().left;
    const offset = slideLeft - trackLeft + this.track.scrollLeft;
    this.track.scrollTo({ left: offset, behavior: 'smooth' });
  }

  /** Width of a single slide (includes gap). */
  _getSlideWidth() {
    const slide = this.slides[0];
    if (!slide) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.offsetWidth + gap;
  }

  /** Index of the most-visible slide (left edge closest to scroll position). */
  _getActiveIndex() {
    const slideWidth = this._getSlideWidth();
    if (!slideWidth) return 0;
    return Math.round(this.track.scrollLeft / slideWidth);
  }

  _onScroll() {
    this._updateState();
  }

  _onResize() {
    this._updateState();
  }

  _updateState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    const activeIndex = this._getActiveIndex();
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
    });
  }

  destroy() {
    this.track.removeEventListener('scroll', this._onScroll);
    this._resizeObserver.disconnect();
  }
}

function initCarousels() {
  document.querySelectorAll('[data-interactive-video-carousel]').forEach((root) => {
    if (!root._ivcInstance) {
      root._ivcInstance = new InteractiveVideoCarousel(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarousels);
} else {
  initCarousels();
}

/* Re-init on Shopify section reload in theme editor */
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-interactive-video-carousel]');
  if (root) {
    if (root._ivcInstance) root._ivcInstance.destroy();
    root._ivcInstance = new InteractiveVideoCarousel(root);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector('[data-interactive-video-carousel]');
  if (root && root._ivcInstance) {
    root._ivcInstance.destroy();
    delete root._ivcInstance;
  }
});
